import type { RunStatus, StepStatus } from '@grafx-vdp/shared';

import { Badge } from '@/components/ui/badge.js';

const PALETTE_BADGE: Record<'success' | 'error' | 'info' | 'warning', string> = {
  success: 'border-transparent bg-[var(--palette-success-light)] text-[var(--palette-success-dark)]',
  error: 'border-transparent bg-[var(--palette-error-light)] text-[var(--palette-error-dark)]',
  info: 'border-transparent bg-[var(--palette-info-light)] text-[var(--palette-info-dark)]',
  warning: 'border-transparent bg-[var(--palette-warning-light)] text-[var(--palette-warning-dark)]',
};

export function RunStatusBadge({ status }: { status: RunStatus }) {
  switch (status) {
    case 'succeeded':
      return (
        <Badge variant="outline" className={PALETTE_BADGE.success}>
          succeeded
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="outline" className={PALETTE_BADGE.error}>
          failed
        </Badge>
      );
    case 'running':
      return (
        <Badge variant="outline" className={PALETTE_BADGE.info}>
          running
        </Badge>
      );
    case 'cancelled':
      return (
        <Badge variant="outline" className={PALETTE_BADGE.warning}>
          cancelled
        </Badge>
      );
    case 'queued':
    default:
      return <Badge variant="secondary">queued</Badge>;
  }
}

/**
 * Derived, plan-aware state for a step shown on the run-detail page. Unlike the
 * recorded `StepStatus`, this also covers steps that have not run yet
 * (`pending`) or are currently executing (`running`).
 */
export type StepPlanState = 'pending' | 'running' | 'succeeded' | 'failed' | 'retrying';

export function StepPlanStatusBadge({ state }: { state: StepPlanState }) {
  switch (state) {
    case 'succeeded':
      return (
        <Badge variant="outline" className={PALETTE_BADGE.success}>
          succeeded
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="outline" className={PALETTE_BADGE.error}>
          failed
        </Badge>
      );
    case 'running':
      return (
        <Badge variant="outline" className={PALETTE_BADGE.info}>
          running
        </Badge>
      );
    case 'retrying':
      return (
        <Badge variant="outline" className={PALETTE_BADGE.warning}>
          retrying
        </Badge>
      );
    case 'pending':
    default:
      return <Badge variant="secondary">pending</Badge>;
  }
}

export function StepStatusBadge({ status }: { status: StepStatus }) {
  switch (status) {
    case 'succeeded':
      return (
        <Badge variant="outline" className={PALETTE_BADGE.success}>
          succeeded
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="outline" className={PALETTE_BADGE.error}>
          failed
        </Badge>
      );
    case 'retrying':
    default:
      return (
        <Badge variant="outline" className={PALETTE_BADGE.warning}>
          retrying
        </Badge>
      );
  }
}
